import type { TrackingType, TrainingSession, TrainingSet } from "./types";

export interface PreviousExerciseRecord {
  sessionId: number;
  trainedOn: string;
  exercise: TrainingSession["exercises"][number];
}

export interface SetPrefill {
  source: "current" | "previous" | null;
  weightKg: number | null;
  reps: number | null;
  durationSeconds: number | null;
  distanceMeters: number | null;
  rpe: number | null;
  rir: number | null;
}

export type RestTimerStatus = "running" | "paused" | "finished";

export interface RestTimerState {
  sessionId: number;
  exerciseId: number;
  exerciseName: string;
  durationSeconds: number;
  remainingSeconds: number;
  endsAt: number | null;
  status: RestTimerStatus;
}

const emptyPrefill: Omit<SetPrefill, "source"> = {
  weightKg: null,
  reps: null,
  durationSeconds: null,
  distanceMeters: null,
  rpe: null,
  rir: null
};

export function findPreviousExercise(
  sessions: TrainingSession[],
  currentSessionId: number,
  exerciseId: number
): PreviousExerciseRecord | null {
  const previousSession = sessions
    .filter(
      (session) =>
        session.id !== currentSessionId &&
        session.endedAt !== null &&
        session.exercises.some((exercise) => exercise.exerciseId === exerciseId)
    )
    .sort((left, right) => right.trainedOn.localeCompare(left.trainedOn) || right.id - left.id)[0];
  if (!previousSession) return null;

  const exercise = previousSession.exercises.find((item) => item.exerciseId === exerciseId);
  return exercise ? { sessionId: previousSession.id, trainedOn: previousSession.trainedOn, exercise } : null;
}

export function getSetPrefill(
  currentSets: TrainingSet[],
  previousSets: TrainingSet[],
  trackingType: TrackingType
): SetPrefill {
  const currentSet = currentSets.at(-1);
  if (currentSet) {
    return {
      source: "current",
      weightKg: currentSet.weightKg,
      reps: currentSet.reps,
      durationSeconds: currentSet.durationSeconds,
      distanceMeters: currentSet.distanceMeters,
      rpe: currentSet.rpe,
      rir: currentSet.rir
    };
  }

  const previousSet = previousSets.at(-1);
  if (previousSet && trackingType === "strength") {
    return {
      ...emptyPrefill,
      source: "previous",
      weightKg: previousSet.weightKg,
      reps: previousSet.reps
    };
  }

  return { ...emptyPrefill, source: null };
}

export function createRestTimer(
  sessionId: number,
  exerciseId: number,
  exerciseName: string,
  durationSeconds: number,
  now = Date.now()
): RestTimerState {
  const duration = Math.max(1, Math.round(durationSeconds));
  return {
    sessionId,
    exerciseId,
    exerciseName,
    durationSeconds: duration,
    remainingSeconds: duration,
    endsAt: now + duration * 1000,
    status: "running"
  };
}

export function reconcileRestTimer(timer: RestTimerState, now = Date.now()): RestTimerState {
  if (timer.status !== "running" || timer.endsAt === null) return timer;
  const remainingSeconds = Math.max(0, Math.ceil((timer.endsAt - now) / 1000));
  if (remainingSeconds === timer.remainingSeconds) return timer;
  if (remainingSeconds === 0) {
    return { ...timer, remainingSeconds: 0, endsAt: null, status: "finished" };
  }
  return { ...timer, remainingSeconds };
}

export function pauseRestTimer(timer: RestTimerState, now = Date.now()): RestTimerState {
  const current = reconcileRestTimer(timer, now);
  return current.status === "running" ? { ...current, endsAt: null, status: "paused" } : current;
}

export function resumeRestTimer(timer: RestTimerState, now = Date.now()): RestTimerState {
  if (timer.status !== "paused" || timer.remainingSeconds <= 0) return timer;
  return { ...timer, endsAt: now + timer.remainingSeconds * 1000, status: "running" };
}

export function adjustRestTimer(timer: RestTimerState, deltaSeconds: number, now = Date.now()): RestTimerState {
  const current = reconcileRestTimer(timer, now);
  const remainingSeconds = Math.max(0, current.remainingSeconds + deltaSeconds);
  if (remainingSeconds === 0) {
    return { ...current, remainingSeconds: 0, endsAt: null, status: "finished" };
  }
  if (current.status === "paused") return { ...current, remainingSeconds };
  return {
    ...current,
    remainingSeconds,
    endsAt: now + remainingSeconds * 1000,
    status: "running"
  };
}

export function resetRestTimer(timer: RestTimerState, now = Date.now()): RestTimerState {
  return createRestTimer(timer.sessionId, timer.exerciseId, timer.exerciseName, timer.durationSeconds, now);
}

export function parseRestTimer(value: string | null, now = Date.now()): RestTimerState | null {
  if (!value) return null;
  try {
    const timer = JSON.parse(value) as Partial<RestTimerState>;
    if (
      !Number.isInteger(timer.exerciseId) ||
      !Number.isInteger(timer.sessionId) ||
      typeof timer.exerciseName !== "string" ||
      !Number.isInteger(timer.durationSeconds) ||
      (timer.durationSeconds ?? 0) <= 0 ||
      !Number.isInteger(timer.remainingSeconds) ||
      (timer.remainingSeconds ?? -1) < 0 ||
      !["running", "paused", "finished"].includes(timer.status ?? "") ||
      (timer.endsAt !== null && (typeof timer.endsAt !== "number" || !Number.isFinite(timer.endsAt)))
    ) {
      return null;
    }
    return reconcileRestTimer(timer as RestTimerState, now);
  } catch {
    return null;
  }
}
